/**
 * Tool definitions. Each tool is a toggle that adds/removes a class on
 * <html> (the "host" page) so styles in HOST_CSS take effect. A tiny
 * number of tools have side effects (reading guide, font scale) — those
 * use the `onEnable`/`onDisable` hooks.
 */
export interface Tool {
  id: string;
  label: string;
  hostClass?: string;
  onEnable?: () => void;
  onDisable?: () => void;
  /** If true, this tool is mutually exclusive with others in the same group */
  group?: string;
}

let readingGuideEl: HTMLDivElement | null = null;
let readingGuideHandler: ((e: MouseEvent) => void) | null = null;

let skipLinkEl: HTMLAnchorElement | null = null;
let srLiveRegion: HTMLDivElement | null = null;

let readAloudHandler: ((e: MouseEvent) => void) | null = null;

export const TOOLS: Tool[] = [
  { id: "high-contrast", label: "High contrast", hostClass: "uq-high-contrast", group: "contrast" },
  { id: "dark-mode", label: "Dark mode", hostClass: "uq-dark-mode", group: "contrast" },
  { id: "readable", label: "Readable font", hostClass: "uq-readable", group: "font" },
  { id: "dyslexia", label: "Dyslexia-friendly font", hostClass: "uq-dyslexia", group: "font" },
  { id: "text-spacing", label: "More text spacing", hostClass: "uq-text-spacing" },
  { id: "links-highlight", label: "Highlight links", hostClass: "uq-links-highlight" },
  { id: "focus-rings", label: "Show focus rings", hostClass: "uq-focus-rings" },
  { id: "stop-animations", label: "Pause animations", hostClass: "uq-stop-animations" },
  { id: "big-cursor", label: "Bigger cursor", hostClass: "uq-big-cursor" },
  {
    id: "screen-reader",
    label: "Screen reader",
    hostClass: "uq-screen-reader",
    onEnable() {
      // Skip-to-content link at the very top of the page — #1 screen reader aid
      skipLinkEl = document.createElement("a");
      skipLinkEl.href = "#main-content";
      skipLinkEl.className = "uq-skip-link";
      skipLinkEl.textContent = "Skip to main content";
      document.body.insertBefore(skipLinkEl, document.body.firstChild);

      // If no #main-content target exists, try to label the main element
      if (!document.getElementById("main-content")) {
        const main = document.querySelector(
          "main, [role='main'], #content, #main, .main-content, .site-content"
        ) as HTMLElement | null;
        if (main && !main.id) main.id = "main-content";
      }

      // ARIA live region so screen readers hear dynamic announcements
      srLiveRegion = document.createElement("div");
      srLiveRegion.setAttribute("role", "status");
      srLiveRegion.setAttribute("aria-live", "polite");
      srLiveRegion.setAttribute("aria-atomic", "true");
      srLiveRegion.style.cssText =
        "position:absolute;width:1px;height:1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;left:-9999px;";
      document.body.appendChild(srLiveRegion);

      setTimeout(() => {
        if (srLiveRegion)
          srLiveRegion.textContent =
            "Screen reader mode enabled. A skip to main content link has been added at the top of the page.";
      }, 150);
    },
    onDisable() {
      skipLinkEl?.remove();
      skipLinkEl = null;
      srLiveRegion?.remove();
      srLiveRegion = null;
    },
  },
  {
    id: "reading-guide",
    label: "Reading guide",
    onEnable() {
      readingGuideEl = document.createElement("div");
      readingGuideEl.className = "uq-reading-guide";
      document.body.appendChild(readingGuideEl);
      readingGuideHandler = (e) => {
        if (readingGuideEl) readingGuideEl.style.top = `${e.clientY - 20}px`;
      };
      document.addEventListener("mousemove", readingGuideHandler);
    },
    onDisable() {
      if (readingGuideHandler) document.removeEventListener("mousemove", readingGuideHandler);
      readingGuideEl?.remove();
      readingGuideEl = null;
      readingGuideHandler = null;
    },
  },
  { id: "hide-images", label: "Hide images", hostClass: "uq-hide-images" },
  { id: "highlight-headings", label: "Highlight headings", hostClass: "uq-highlight-headings" },
  {
    id: "read-aloud",
    label: "Read aloud (click text)",
    hostClass: "uq-read-aloud",
    onEnable() {
      if (!("speechSynthesis" in window)) return;
      readAloudHandler = (e) => {
        const t = e.target as HTMLElement | null;
        // Never read our own UI (clicks from the shadow root retarget to the mount).
        if (!t || t.id === "uniqu-widget-root" || t.closest?.("#uniqu-widget-root")) return;
        const text = (t.innerText || t.textContent || "").trim();
        if (!text) return;
        try {
          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(new SpeechSynthesisUtterance(text.slice(0, 4000)));
        } catch {
          /* speech can be blocked; ignore */
        }
      };
      document.addEventListener("click", readAloudHandler, true);
    },
    onDisable() {
      if (readAloudHandler) document.removeEventListener("click", readAloudHandler, true);
      readAloudHandler = null;
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* ignore */
      }
    },
  },
  {
    id: "pause-media",
    label: "Pause audio & video",
    onEnable() {
      document.querySelectorAll<HTMLMediaElement>("video, audio").forEach((m) => {
        try {
          m.pause();
        } catch {
          /* ignore */
        }
      });
    },
  },
];

/**
 * One-tap accessibility profiles — curated bundles of tools for the most
 * common needs. Each maps to existing tools so there's no overlay magic:
 * a profile just flips the same honest toggles a user could set by hand.
 */
export interface Profile {
  id: string;
  label: string;
  /** Tool ids to enable. */
  tools: string[];
  /** Optional font scale to apply. */
  fontScale?: number;
}

export const PROFILES: Profile[] = [
  { id: "vision", label: "Low vision", tools: ["high-contrast", "links-highlight", "focus-rings"], fontScale: 1.3 },
  { id: "dyslexia", label: "Dyslexia", tools: ["dyslexia", "text-spacing", "reading-guide"] },
  { id: "motor", label: "Motor & focus", tools: ["big-cursor", "focus-rings", "stop-animations", "reading-guide"] },
  { id: "seizure", label: "Seizure safe", tools: ["stop-animations", "pause-media", "hide-images"] },
  { id: "screen-reader", label: "Screen reader", tools: ["screen-reader", "links-highlight", "focus-rings"] },
];

export function setFontScale(scale: number) {
  const html = document.documentElement;
  if (scale === 1) {
    html.removeAttribute("data-uq-font-scale");
    html.style.removeProperty("--uq-font-scale");
  } else {
    html.setAttribute("data-uq-font-scale", String(scale));
    html.style.setProperty("--uq-font-scale", String(scale));
  }
}
